import axios from "axios";
import boApiCaller from "./boApiCaller";
import bffApiCaller from "./bffApiCaller";
import store from '../store/store';
import { showMessage, hideMessage } from '../store/globalSlice';

export interface Offer {
    offerId: string;
    lender: {
        lenderId: string;
        lenderName: string;
        logoUrl: string;
    };
    customerSegmentText: string;
    interestDisplayText: string;
    offerName: string;
    amount: {
        totalAmount: number | string;
        currency: string;
        displayLabel: string;
    };
    highlights: string | null;
    cta: {
        buttonText: string;
        actionValue: string;
    };
    conditionsUrl: string;
    isActive: boolean | string;    // Backend sends string "true"/"false"
}

export interface OffersResponse {
    offers: Offer[];
}

export interface ComplianceResponse {
    fund: string;
    fundDate: string;
}

export interface Vendor {
    userId: string;
    legalName: string;
    taxNumber: string;
    phoneNumber: string;
    ownerFullName: string;
    birthDate: string;
}

export interface RequiredConsent {
    consentId: string | number;
    documentUrl: string;
    mandatory: boolean;
    type: string;
    documentContent: string;
}

export interface LoanApplicationFormParameters {
    code?: string;
    message?: string;
    offerId: string;
    vendor: Vendor;
    requiredConsents: RequiredConsent[];
}

export interface PostLoanApplicationRequest {
    offerId: string;
    userId: string;
    formData: {
        trIdentificationNumber?: string;
        legalName?: string;
        ownerName?: string;
        phoneNumber?: string;
        birthDate?: string;
        requestedCreditAmount: number;
        applicationReason: string;
    };
    consents: {
        consentId: string;
        documentUrl: string;
        mandatory: boolean;
        isApproved: boolean;
        approvedAt: string;
    }[];
}

export interface PostLoanApplicationResponse {
    code: string;
    message: string;
    applicationId?: string;
    offerId?: string;
    traceId?: string;
    otpID?: string;
    url?: string;
    qrData?: string;
}

const handleRateLimitError = (error: any) => {
    if (error?.response?.status === 429) {
        store.dispatch(showMessage({
            title: "İşlem Sınırı Aşıldı",
            text: "Çok fazla deneme yaptınız. Lütfen daha sonra tekrar deneyiniz.",
            btnPrimaryText: "Ana Sayfaya Dön",
            btnPrimaryCallback: () => {
                store.dispatch(hideMessage());
                window.location.href = '/loan';
            }
        }));
    }
};

export const fetchCompliance = async (): Promise<ComplianceResponse> => {
    try {
        const response = await boApiCaller.get<ComplianceResponse>('/api/merchant/compliance-detail');
        return response.data;
    } catch (error) {
        console.error('Error fetching system configuration:', error);
        throw error;
    }
};

export const fetchOffers = async (signal: AbortSignal): Promise<OffersResponse> => {
    try {
        const response = await bffApiCaller.get<OffersResponse>(`/api/v1/vendor/loan-offers`, { signal });
        return response.data;
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
        } else {
            console.error('Error fetching offers:', error);
            handleRateLimitError(error);
        }
        throw error;
    }
};

export const getLoanApplicationFormParameters = async (identityNumber: string, offerId: string, phoneNumber?: string): Promise<LoanApplicationFormParameters> => {
    try {
        const params: any = {
            offerId: offerId,
            identityNumber: identityNumber
        };
        if (phoneNumber) {
            params.phoneNumber = phoneNumber;
        }
        const response = await bffApiCaller.get<LoanApplicationFormParameters>('/api/v1/vendor/loan-offer-form', {
            params,
            withCredentials: true
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching loan application form parameters:', error);
        handleRateLimitError(error);
        throw error;
    }
};

export const postLoanApplication = async (request: PostLoanApplicationRequest): Promise<PostLoanApplicationResponse> => {
    try {
        const response = await bffApiCaller.post<PostLoanApplicationResponse>('/api/v1/vendor/loan-offer-form', request, {
            withCredentials: true
        });
        return response.data;
    } catch (error) {
        console.error('Error posting loan application:', error);
        handleRateLimitError(error);
        throw error;
    }
};

export interface SendOtpRequest {
    applicationId: string;
    trIdentificationNumber: string;
    birthDate: string;
    phoneCountryCode: string;
    phoneAreaCode: string;
    phoneNumber: string;
    gdprConfirmation: string;
    languageCode: string;
    deviceIpAddress?: string;
}

export interface SendOtpResponse {
    code: string;
    message: string;
    applicationId?: string;
    offerId?: string;
}

export const sendOtp = async (request: SendOtpRequest): Promise<SendOtpResponse> => {
    try {
        const response = await bffApiCaller.post<SendOtpResponse>('/api/v1/vendor/send-otp', request, {
            withCredentials: true
        });
        return response.data;
    } catch (error) {
        console.error('Error sending OTP:', error);
        handleRateLimitError(error);
        throw error;
    }
};

export interface GetPhoneNumberResponse {
    code: string;
    message: string;
    phoneNumber?: string;
}

export const getPhoneNumber = async (identityNumber: string, offerId: string): Promise<GetPhoneNumberResponse> => {
    try {
        const response = await bffApiCaller.get<GetPhoneNumberResponse>('/api/v1/vendor/phone', {
            params: { identityNumber, offerId },
            withCredentials: true
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching phone number:', error);
        handleRateLimitError(error);
        throw error;
    }
};

export interface VerifyPhoneNumberRequest {
    identityNumber: string;
    offerId: string;
    phoneNumber: string;
}

export interface VerifyPhoneNumberResponse {
    code: string;
    message: string;
}

export const verifyPhoneNumber = async (request: VerifyPhoneNumberRequest): Promise<VerifyPhoneNumberResponse> => {
    try {
        const response = await bffApiCaller.get<VerifyPhoneNumberResponse>('/api/v1/vendor/phone/verify', {
            params: request,
            withCredentials: true
        });
        return response.data;
    } catch (error) {
        console.error('Error verifying phone number:', error);
        handleRateLimitError(error);
        throw error;
    }
};

export interface ApiPutLoanApplicationRequestFormDataDto {
    trIdentificationNumber?: string;
    phoneNumber?: string;
    birthDate?: string;
    requestedCreditAmount?: string;
    applicationReason?: string;
}

export interface ApiPutLoanApplicationRequestConsentsDto {
    consentId: string;
    isApproved: string;
}

export interface ApiPutLoanApplicationRequest {
    applicationId: string;
    userId: string;
    offerId: string;
    formData: ApiPutLoanApplicationRequestFormDataDto;
    consents: ApiPutLoanApplicationRequestConsentsDto[];
    otp: string;
    languageCode: string;
    deviceIpAddress?: string;
}

export interface ApiPutLoanApplicationResponse {
    code?: string;
    message?: string;
    applicationId?: string;
    offerId?: string;
    eligibilityStatus?: string;
    customerJourney?: string;
    lenderResponse?: {
        code?: string;
        message?: string;
    };
    redirect?: {
        type?: "MOBILE" | "WEB" | "BOTH";
        mobileUrl?: string | null;
        webUrl?: string | null;
        htmlContent?: string | null;
    };
    availableCreditLimit?: string;
    [key: string]: any;
}

export const putLoanApplication = async (request: ApiPutLoanApplicationRequest): Promise<ApiPutLoanApplicationResponse> => {
    try {
        const response = await bffApiCaller.put<ApiPutLoanApplicationResponse>('/api/v1/vendor/loan-application', request, {
            withCredentials: true
        });
        return response.data;
    } catch (error) {
        console.error('Error putting loan application:', error);
        handleRateLimitError(error);
        throw error;
    }
};

